import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Mail, Lock, ArrowLeft, Link2, Check } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { WebsiteSignup, NurseryCustomer, NurseryCustomerTier } from '../../types/nursery';

const STATUS_TABS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Linked', value: 'linked' },
];

function signupName(s: WebsiteSignup): string {
  const full = [s.first_name, s.last_name].filter(Boolean).join(' ').trim();
  return full || s.organisation_name || s.email;
}

function LinkPanel({ signup, customers, tiers, onLinked }: {
  signup: WebsiteSignup;
  customers: NurseryCustomer[];
  tiers: NurseryCustomerTier[];
  onLinked: (updated: WebsiteSignup) => void;
}) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [newName, setNewName] = useState(signup.organisation_name || '');
  const [tierId, setTierId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (mode === 'existing' && !customerId) {
      setError('Select a customer to link.');
      return;
    }
    if (mode === 'new' && !newName.trim()) {
      setError('Enter a customer name.');
      return;
    }
    setSaving(true);
    try {
      const res = await nurseryService.linkWebsiteSignup(signup.id, {
        ...(mode === 'existing' ? { customer_id: Number(customerId) } : { customer_name: newName.trim() }),
        ...(tierId ? { customer_tier_id: Number(tierId) } : {}),
      });
      if (res.success) {
        onLinked(res.data as WebsiteSignup);
      } else {
        setError((res as { error?: { message?: string } }).error?.message || 'Failed to link');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-neutral-100 pt-3 space-y-3">
      <div className="flex gap-1">
        {(['existing', 'new'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              mode === m ? 'bg-primary-600 text-white font-medium' : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            {m === 'existing' ? 'Link to existing customer' : 'Create new customer'}
          </button>
        ))}
      </div>

      {mode === 'existing' ? (
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : '')}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select a customer…</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.linked_org_id ? ' (already linked)' : ''}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Customer / business name"
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      )}

      <select
        value={tierId}
        onChange={(e) => setTierId(e.target.value ? Number(e.target.value) : '')}
        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <option value="">No pricing tier</option>
        {tiers.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      {error && <p className="text-xs text-red-500">{error}</p>}

      <Button variant="primary" size="sm" onClick={submit} disabled={saving}>
        {saving ? 'Linking…' : <>Link account <Check className="h-4 w-4" /></>}
      </Button>
    </div>
  );
}

function WebsiteSignupsList() {
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [status, setStatus] = useState('pending');
  const [signups, setSignups] = useState<WebsiteSignup[]>([]);
  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getWebsiteSignups(status);
      if (res.success) setSignups(res.data || []);
      else setError((res as { error?: { message?: string } }).error?.message || 'Failed to load');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    nurseryService.getCustomers({ active: true }).then((r) => { if (r.success) setCustomers(r.data || []); });
    nurseryService.getCustomerTiers().then((r) => { if (r.success) setTiers(r.data || []); });
  }, []);

  if (licenseLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Manager license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Linking website signups to customer records requires an active <span className="font-medium">Nursery Manager</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to nursery
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Website Signups"
        subtitle="Self-registered website customers — link them to your records"
        icon={<Mail className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex gap-1 flex-wrap mb-6">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              status === tab.value ? 'bg-primary-600 text-white font-medium' : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : signups.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No {status} signups</p>
          </div>
        ) : (
          <div className="space-y-3">
            {signups.map((s) => (
              <div key={s.id} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-800 text-sm">{signupName(s)}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{s.email}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-neutral-400">
                      {s.customer_number && <span>Customer #: <span className="text-neutral-600 font-medium">{s.customer_number}</span></span>}
                      <span>Signed up {s.created_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                  {s.status === 'linked' ? (
                    <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600">
                      Linked{s.linked_customer_name ? ` · ${s.linked_customer_name}` : ''}
                    </span>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                      <Link2 className="h-4 w-4 mr-1" /> Link
                    </Button>
                  )}
                </div>

                {s.status === 'pending' && expanded === s.id && (
                  <LinkPanel
                    signup={s}
                    customers={customers}
                    tiers={tiers}
                    onLinked={(updated) => {
                      setExpanded(null);
                      setSignups((prev) => prev.filter((x) => x.id !== updated.id));
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default WebsiteSignupsList;
